/**
 * /models — user-mode view of LLM models, three-section layout.
 *
 * Per Ean 2026-06-10 (item 3 of punt-list close-out): same shape as
 * /connectors. Three sections: Yours / Available in your workspace / Coming
 * soon. Pulls from /api/me/models/catalog which returns one row per
 * llm__model_template + per-user flags.
 *
 * Drill-down: /models/:slug renders ModelDetail with Lightning record page.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import {
  fetchMyModelCatalog,
  ApiError,
  type MyModelCatalogRow,
} from '../lib/api.ts';
import { SkeletonCardGrid } from '../components/Skeleton.tsx';
import { useDelayedFlag } from '../hooks/useDelayedFlag.ts';
import { ObjectIcon } from '../components/ObjectIcon.tsx';
import { BrandLogo } from '../components/BrandLogo.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Pill } from '../components/StatusPill.tsx';
import { RequestModal } from '../components/RequestModal.tsx';

/**
 * Push #76G — humanize D-150 recommended_for tag slugs into the short
 * uppercase labels that match the chat-rail + Library/market chrome
 * (Push #75). Centralized here so the Models page tiles read the same
 * "REASONING / CODE / FAST" pills as the marketplace tiles.
 */
const MODEL_TAG_LABELS: Record<string, string> = {
  reasoning: 'REASONING',
  fast: 'FAST',
  image_generation: 'IMAGE',
  code: 'CODE',
  cost_optimized: 'BUDGET',
  regulated: 'REGULATED',
};

function ModelTagPills({ tags }: { tags: string[] | null }) {
  if (tags === null || tags.length === 0) return null;
  const labels = tags.map((t) => MODEL_TAG_LABELS[t] ?? t.toUpperCase()).slice(0, 4);
  // Round 9 Item 52: routes through canonical .pill-badge (app.css) so all
  // FAST/REASONING/CODE/BUDGET microbadges share the same shape + radius +
  // letter-spacing as the Market tile chips + TierDetail "Default" badge.
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
      {labels.map((label) => (
        <span key={label} className="pill-badge">
          {label}
        </span>
      ))}
    </div>
  );
}

export function Models() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [list, setList] = useState<MyModelCatalogRow[] | null>(null);
  // Per Ean 2026-06-10 card-density: collapse each section to 6 tiles.
  const [showAllYours, setShowAllYours] = useState(false);
  const [showAllAvailable, setShowAllAvailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestSlug, setRequestSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyModelCatalog()
      .then((d) => {
        if (!cancelled) setList(d.templates);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load models.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // D-156 catalog IA: 3-bucket user-mode projection. Public catalog (renamed
  // from comingSoon) now carries states 3 + 4 — active uninstalled templates
  // AND coming_soon roadmap teasers. Card differentiation by is_preview.
  const sections = useMemo(() => {
    if (list === null) return { yours: [], available: [], comingSoon: [] };
    const yours: MyModelCatalogRow[] = [];
    const available: MyModelCatalogRow[] = [];
    const publicCatalog: MyModelCatalogRow[] = [];
    for (const t of list) {
      if (t.is_mine) {
        yours.push(t);
      } else if (t.is_installed) {
        available.push(t);
      } else {
        // State 3 (active uninstalled) OR State 4 (preview/coming_soon) —
        // both surface in Public catalog with chip + action differentiation.
        publicCatalog.push(t);
      }
    }
    return { yours, available, comingSoon: publicCatalog };
  }, [list]);

  if (user === null) return null;

  const loaded = list !== null;
  // D-159: workspace-scoped emptiness. Public catalog moved to /catalog.

  return (
    <div>
      <div className="lightning-highlight">
        <div className="lightning-highlight-left">
          <ObjectIcon kind="models" size="lg" />
          <div className="lightning-highlight-text">
            <h1 className="lightning-title">My models</h1>
            <p
              className="muted"
              style={{ margin: '6px 0 0', fontSize: '13px', maxWidth: '72ch', lineHeight: 1.45 }}
            >
              AI models to use in Olytic chat, or through Claude Desktop, ChatGPT, or any MCP-aware AI client. See what you have, what your workspace offers, and what's on the way.
            </p>
          </div>
        </div>
        <div className="lightning-highlight-actions">
          <Link to="/market?tab=models" className="btn btn-secondary">
            Browse market
          </Link>
          <Link to="/chat" className="btn btn-primary">
            Go to chat
          </Link>
        </div>
      </div>

      {error !== null && (
        <div className="callout callout-danger" style={{ marginBottom: '16px' }}>
          <p className="callout-body">{error}</p>
        </div>
      )}

      {!loaded && error === null && <ModelsSkeleton />}

      {loaded && (
        <>
          <section className="catalog-section">
            <div className="catalog-section-heading">
              <h2 className="catalog-section-title">
                What you can use <span className="catalog-section-count">({sections.yours.length})</span>
              </h2>
              <p className="catalog-section-helper">
                Assigned to you. Click any tile to see capabilities, pricing, and how the model behaves.
              </p>
              {sections.yours.length > 6 && (
                <button type="button" className="btn-link" onClick={() => setShowAllYours((v) => !v)} style={{ fontSize: 12, marginTop: 4 }}>
                  {showAllYours ? 'Show fewer' : `Show all (${sections.yours.length})`}
                </button>
              )}
            </div>
            {sections.yours.length === 0 ? (
              // Push 5-F (2026-07-11): migrate legacy `cta` -> structured `primaryAction`.
              <EmptyState
                kind="models"
                title="No models assigned to you yet"
                body="Browse the market to install one, or ask your admin to assign one from the workspace list below."
                primaryAction={{ label: 'Browse the market', to: '/market?tab=models' }}
              />
            ) : (
              <div className="tile-grid">
                {(showAllYours ? sections.yours : sections.yours.slice(0, 6)).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className="tile"
                    onClick={() =>
                      m.instance_short_id !== null && navigate(`/models/${m.instance_short_id}`)
                    }
                  >
                    <div className="tile-header">
                      {/* Push #16 rule #2 + Round 12: brand-faithful provider
                         logo on each model card (Anthropic mark, OpenAI knot,
                         Google G, AWS smile). BrandLogo falls back to the
                         AppIcon initial tile for unknown providers. */}
                      <BrandLogo
                        slug={m.provider_slug}
                        name={m.provider_display_name ?? m.provider_slug}
                        size="sm"
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="tile-name">{m.display_name}</p>
                        <p className="tile-meta">{m.provider_display_name ?? m.provider_slug}</p>
                      </div>
                    </div>
                    {m.description !== null && <p className="tile-tagline">{m.description}</p>}
                    <ModelTagPills tags={m.recommended_for} />
                    <div className="tile-footer">
                      <Pill kind="info">Assigned</Pill>
                      <span className="muted" style={{ fontSize: '12px' }}>Open →</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="catalog-section">
            <div className="catalog-section-heading">
              <h2 className="catalog-section-title">
                What you could use <span className="catalog-section-count">({sections.available.length})</span>
              </h2>
              <p className="catalog-section-helper">
                Installed in your workspace but not assigned to you. Ask your admin for access.
              </p>
              {sections.available.length > 6 && (
                <button type="button" className="btn-link" onClick={() => setShowAllAvailable((v) => !v)} style={{ fontSize: 12, marginTop: 4 }}>
                  {showAllAvailable ? 'Show fewer' : `Show all (${sections.available.length})`}
                </button>
              )}
            </div>
            {sections.available.length === 0 ? (
              // Push 5-F (2026-07-11): migrate legacy `cta` -> structured `secondaryAction`.
              <EmptyState
                kind="models"
                title="Nothing else is installed in your workspace"
                body="Browse the market to suggest a new model for your admin to install."
                secondaryAction={{ label: 'Browse the market', to: '/market?tab=models' }}
              />
            ) : (
              <div className="tile-grid">
                {(showAllAvailable ? sections.available : sections.available.slice(0, 6)).map((m) => (
                  <div key={m.id} className="tile tile-static">
                    <div className="tile-header">
                      <BrandLogo
                        slug={m.provider_slug}
                        name={m.provider_display_name ?? m.provider_slug}
                        size="sm"
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="tile-name">{m.display_name}</p>
                        <p className="tile-meta">{m.provider_display_name ?? m.provider_slug}</p>
                      </div>
                    </div>
                    {m.description !== null && <p className="tile-tagline">{m.description}</p>}
                    <ModelTagPills tags={m.recommended_for} />
                    <div className="tile-footer">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setRequestSlug(m.model_id)}
                      >
                        Request access
                      </button>
                      <span className="muted" style={{ fontSize: '12px' }}>{m.model_id}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* D-159: Public catalog (states 3+4) moved to /catalog. The Catalog
             top-level nav is the marketplace; pillar pages stay workspace-scoped. */}

          <div className="trust-whisper trust-whisper--footer">
            Any model here follows the same permissions · Switching the model never widens what your AI can touch
          </div>
        </>
      )}

      <RequestModal
        open={requestSlug !== null}
        onClose={() => setRequestSlug(null)}
        destination="admin"
        initialKind="feedback"
        {...(requestSlug !== null ? { subjectHint: `Model request: ${requestSlug}` } : {})}
      />
    </div>
  );
}

/**
 * ModelsSkeleton — pillar-page placeholder. Two sections mirror the live
 * layout. 200ms delay so fast loads never flicker.
 */
function ModelsSkeleton() {
  const show = useDelayedFlag(true);
  if (!show) return <div style={{ minHeight: '40vh' }} />;
  return (
    <>
      <section className="catalog-section">
        <SkeletonCardGrid count={6} />
      </section>
      <section className="catalog-section">
        <SkeletonCardGrid count={3} />
      </section>
    </>
  );
}
